import styled, { css } from "styled-components";

type SidebarProps = {
  $isSidebarCollapsed?: boolean;
};

// Reusable CSS styles
const flexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Wrapper for registration container
export const SignupWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  ${flexCenter}
`;

// Left panel wrapper
export const StepperWrapper = styled.div`
  width: 50%;
  height: 100vh;
  ${flexCenter}

  @media (max-width: 767px) {
    display: none;
  }
`;

// Main container
export const RegistrationContainer = styled.div<SidebarProps>`
  display: flex;
  width: 100%;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};

  @media screen and (min-width: 1441px) {
    width: 1280px !important;
  }

  @media (max-width: 767px) {
    flex-direction: column;

    > * {
      width: 100%;
      max-width: 100%;
    }
  }

  @media (max-width: 375px) {
    min-width: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "auto" : "295px !important"};
    overflow-x: ${({ $isSidebarCollapsed }) =>
      $isSidebarCollapsed ? "hidden" : "auto"};
  }
`;

// Left side with stepper and illustrations
export const StepperSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.background};

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 375px) {
    padding: ${({ theme }) => theme.spacing.sm};
    min-width: 260px;
  }
`;

// Logo container
export const LogoContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  img {
    height: 33px;
  }

  @media (max-width: 375px) {
    margin-bottom: ${({ theme }) => theme.spacing.lg};

    img {
      height: 30px;
    }
  }
`;

// Illustration container
export const IllustrationContainer = styled.div`
  width: 100%;

  margin-bottom: ${({ theme }) => theme.spacing.xl};

  img {
    max-width: 100%;
    height: auto;
    max-height: 300px;
    margin-left: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: 767px) {
    margin-bottom: ${({ theme }) => theme.spacing.lg};

    img {
      max-height: 250px;
    }
  }

  @media (max-width: 375px) {
    margin-bottom: ${({ theme }) => theme.spacing.md};

    img {
      max-height: 200px;
    }
  }
`;

// Text content for the left side
export const StepperTextContent = styled.div`
  text-align: left;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

export const Typography = styled.div`
  font-weight: 400 !important;
  font-size: 16px !important;
  line-height: 24px !important;
  color: #374151 !important;
  margin: 0 !important;
`;

export const HeaderText = styled.div`
  font-size: 32px;
  font-weight: 400;
  line-height: 40px;
  color: #1f2937;
  margin-bottom: 8px;
`;

export const PrimaryText = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

export const NormalText = styled.span`
  color: #222f24;
`;

export const StepperTitle = styled.h2`
  font-size: 32px;
  font-weight: ${({ theme }) => theme.fontWeights.normal};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.text};

  @media (max-width: 767px) {
    font-size: ${({ theme }) => theme.fontSizes.xxlarge};
  }

  @media (max-width: 375px) {
    font-size: ${({ theme }) => theme.fontSizes.xlarge};
  }
`;

export const StepperDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.colors.darkGray};
  margin: 0;

  @media (max-width: 375px) {
    font-size: ${({ theme }) => theme.fontSizes.small};
  }
`;

// Progress indicators
export const ProgressIndicators = styled.div`
  display: flex;
  justify-content: left;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: auto;
`;

export const ProgressDot = styled.div<{ $active: boolean }>`
  width: 133px;
  height: 6px;
  background-color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.gray};
  transition: background-color 0.3s ease;
`;

// Right side with form content
export const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.backgroundLightPrimary};
  width: 50%;
  overflow:hidden;

  @media (max-width: 767px) {
    width: 100%
  }
`;

// Progress bar
export const ProgressBarContainer = styled.div`
  width: 100%;
  height: 10px;
  background: ${({ theme }) => theme.colors.background};
border-top-right-radius: 5px;
border-bottom-right-radius: 5px;


  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: 375px) {
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

export const ProgressBar = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => `${$progress}%`};
  background-color: ${({ theme }) => theme.colors.secondary};
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
  transition: width 0.3s ease;
`;

// Form elements
export const FormContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`;

export const FormTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.xlarge};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 375px) {
    font-size: ${({ theme }) => theme.fontSizes.large};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

export const FormSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.colors.darkGray};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 375px) {
    font-size: ${({ theme }) => theme.fontSizes.small};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

export const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const InputField = styled.div`
  position: relative;
  width: 100%;
`;

export const Input = styled.input<{ $hasError?: boolean; marginLeft?: string }>`
  width: 100%;
  height: 50px;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border: 1px solid #e2e2e2;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  background-color: ${({ theme }) => theme.colors.background};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &::placeholder {
    color: #aaaaaa;
  }
`;

export const InputIcon = styled.div`
  position: absolute;
  top: 50%;
  left: ${({ theme }) => theme.spacing.sm};
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.darkGray};
`;

export const InputWithIcon = styled(Input)`
  padding-left: 40px;
`;

export const PhoneInputContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const PhonePrefix = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding: 0 ${({ theme }) => theme.spacing.sm};
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid #e2e2e2;
  border-right: none;
  border-radius: ${({ theme }) =>
    `${theme.borderRadius.small} 0 0 ${theme.borderRadius.small}`};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.colors.text};
    margin-top: -1px;

`;

export const PhoneInput = styled(Input)`
  border-radius: ${({ theme }) =>
    `0 ${theme.borderRadius.small} ${theme.borderRadius.small} 0`};
`;

export const Select = styled.select`
  width: 100%;
  height: 50px;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border: 1px solid #e2e2e2;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  background-color: ${({ theme }) => theme.colors.background};
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235A5A5A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right ${({ theme }) => theme.spacing.md} center;
  background-size: 16px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const Checkbox = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  input {
    margin-right: ${({ theme }) => theme.spacing.sm};
  }

  label {
    font-size: ${({ theme }) => theme.fontSizes.small};
    color: ${({ theme }) => theme.colors.darkGray};
  }
`;

export const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.fontSizes.small};
  margin-top: ${({ theme }) => theme.spacing.xs};
  margin-bottom: 0;
`;

// OTP Input
export const OtpInputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

export const OtpInputField = styled.input`
  width: 44px;
  height: 50px;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.large};
  border: 1px solid #c8c8c8;
  border-radius: ${({ theme }) => theme.borderRadius.small};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const ResendLink = styled.a`
  color: ${({ theme }) => theme.colors.secondary};
  font-size: ${({ theme }) => theme.fontSizes.small};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

// File upload
export const UploadContainer = styled.div`
  border: 1px dashed ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.background};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const UploadText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.colors.darkGray};
`;

export const UploadIcon = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 24px;
`;

// Navigation buttons
export const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 375px) {
    margin-top: ${({ theme }) => theme.spacing.lg};
  }
`;

// Form description
export const FormDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.colors.darkGray};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: 375px) {
    font-size: ${({ theme }) => theme.fontSizes.small};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
  }
`;

// Two column layout
export const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

// File upload components
export const FileUploadContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border: 2px dashed ${({ theme }) => theme.colors.gray};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background-color: ${({ theme }) => theme.colors.background};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &.drag-active {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.lightGray};
  }
`;

export const FileUploadInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

export const FileUploadLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  width: 100%;
`;

export const FileUploadIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.lightGray};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  svg {
    width: 30px;
    height: 30px;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const FileUploadText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.colors.darkGray};
  text-align: center;
  margin: 0;

  span {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
  }
`;

// File preview components
export const FilePreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-top: ${({ theme }) => theme.spacing.md};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

export const FilePreviewName = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

export const FilePreviewSize = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.darkGray};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

export const FilePreviewRemove = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.error};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
`;

// OTP components
export const OtpContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 375px) {
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

export const OtpInput = styled.input`
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.large};
  border: 1px solid ${({ theme }) => theme.colors.gray};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-right: ${({ theme }) => theme.spacing.sm};

  &:last-child {
    margin-right: 0;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
  }

  @media (max-width: 375px) {
    width: 40px;
    height: 40px;
    font-size: ${({ theme }) => theme.fontSizes.medium};
  }
`;

export const ResendButton = styled.button<{
  $disabled?: boolean;
  $isActive?: boolean;
}>`
  background: none;
  border: none;
  color: ${({ $disabled, $isActive, theme }) =>
    $disabled
      ? theme.colors.gray
      : $isActive
      ? theme.colors.primary
      : theme.colors.gray};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: ${({ $disabled, $isActive }) =>
    $disabled ? "not-allowed" : $isActive ? "pointer" : "not-allowed"};
  padding: 0;
  margin-top: ${({ theme }) => theme.spacing.md};

  &:hover {
    opacity: ${({ $disabled }) => ($disabled ? 1 : 0.8)};
  }

  @media (max-width: 375px) {
    margin-top: ${({ theme }) => theme.spacing.lg};
  }
`;

export const BackButton = styled.button`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e2e2e2;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;

  &:hover {
    background-color: #d2d2d2;
  }
`;

export const NextButton = styled.button`
  min-width: 120px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;
